/**
 * Mirrors mof-strapi/src/auth/permissions.ts assignable-permission rules for the SPA.
 * Keep in sync when backend catalogue changes.
 */

export const SUPER_ADMIN_ONLY_PERMISSIONS: string[] = [
  "users:read",
  "users:create",
  "users:update",
  "users:deactivate",
  "users:assign-roles",
  "users:manage-permissions",
  "users:manage-editor-permissions",
  "system:manage-settings",
  "system:backup-restore",
  "obs:manage",
  "delegations:manage",
  "faq:manage",
  "security:manage-tokens",
];

export const EDITOR_ASSIGNABLE_PERMISSIONS: string[] = [
  "content:create-draft",
  "content:edit",
  "content:delete-draft",
  "content:submit",
  "content:review",
  "content:approve",
  "content:publish",
  "documents:upload",
  "documents:read",
  "documents:manage",
  "support:access",
];

/**
 * Account upkeep Super Admin may delegate to a Sub-Admin. Off by default; the backend confines the
 * holder to Editor accounts in their own departments (no role, department or permission changes).
 */
export const SUB_ADMIN_DELEGABLE_USER_PERMISSIONS: string[] = [
  "users:read",
  "users:create",
  "users:update",
  "users:deactivate",
  // Lets the holder edit per-user permission overrides of Editors only (never their own).
  "users:manage-editor-permissions",
];

export const SUB_ADMIN_ASSIGNABLE_PERMISSIONS: string[] = [
  ...EDITOR_ASSIGNABLE_PERMISSIONS,
  ...SUB_ADMIN_DELEGABLE_USER_PERMISSIONS,
  "system:view-dashboard",
  "system:view-analytics",
  "system:view-audit-logs",
];

/** Stable panel order — content ownership, documents, support, then admin keys. */
export const PERMISSION_PANEL_ORDER: string[] = [
  "content:create-draft",
  "content:edit",
  "content:delete-draft",
  "content:submit",
  "content:review",
  "content:approve",
  "content:publish",
  "documents:upload",
  "documents:read",
  "documents:manage",
  "support:access",
  "users:read",
  "users:create",
  "users:update",
  "users:deactivate",
  "users:manage-editor-permissions",
  "system:view-dashboard",
  "system:view-analytics",
  "system:view-audit-logs",
];

const ROLE_ASSIGNABLE: Record<string, string[]> = {
  EDITOR: EDITOR_ASSIGNABLE_PERMISSIONS,
  SUB_ADMIN: SUB_ADMIN_ASSIGNABLE_PERMISSIONS,
  SUPER_ADMIN: [], // read-only in UI; full list comes from matrix API
};

export function getAssignablePermissions(role: string): string[] {
  if (role === "SUPER_ADMIN") return [];
  return ROLE_ASSIGNABLE[role] ?? EDITOR_ASSIGNABLE_PERMISSIONS;
}

export function isPermissionAssignableToRole(
  role: string,
  permission: string,
): boolean {
  if (role === "SUPER_ADMIN") return false;
  return getAssignablePermissions(role).includes(permission);
}

export function filterPermissionsForRole(
  role: string,
  permissions: string[],
): string[] {
  if (role === "SUPER_ADMIN") return [];
  const assignable = new Set(getAssignablePermissions(role));
  return permissions.filter((p) => assignable.has(p));
}
